import { Fragment, useState } from 'react';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { ChevronDown } from 'lucide-react';
import { cn } from '@mlm/ui';
import { PageHeader } from './_shared';
import { JOURNAL, PERIOD_LABEL, peso } from './sample-data';

export function JournalPage() {
  // The revenue entry opens by default — it is the multi-leg one worth reading.
  const [openEntry, setOpenEntry] = useState<string | null>('JE-00419');

  return (
    <div className="space-y-4">
      <PageHeader
        title="General Journal"
        subtitle={`${PERIOD_LABEL} · every entry posted automatically from a source document`}
      />

      <Card>
        <CardHeader>
          <CardTitle>
            Entries{' '}
            <span className="text-sm font-normal text-muted-foreground">
              (showing {JOURNAL.length} most recent)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="w-32 px-4 py-2">Entry no.</th>
                <th className="w-28 px-4 py-2">Date</th>
                <th className="px-4 py-2">Memo</th>
                <th className="w-44 px-4 py-2">Source</th>
                <th className="w-40 px-4 py-2 text-right">Amount</th>
                <th className="w-10 px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {JOURNAL.map((entry) => {
                const open = openEntry === entry.entryNo;
                return (
                  <Fragment key={entry.entryNo}>
                    <tr
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                      onClick={() => setOpenEntry(open ? null : entry.entryNo)}
                    >
                      <td className="px-4 py-2 font-mono text-xs">{entry.entryNo}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{entry.date}</td>
                      <td className="px-4 py-2">{entry.memo}</td>
                      <td className="px-4 py-2">
                        <Badge variant={entry.source === 'Manual' ? 'outline' : 'secondary'}>
                          {entry.source}
                        </Badge>
                        <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                          {entry.sourceRef}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {peso(entry.amount)}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        <ChevronDown
                          className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')}
                        />
                      </td>
                    </tr>
                    {open ? (
                      <tr className="border-b last:border-0">
                        <td colSpan={6} className="bg-muted/20 px-4 py-3">
                          <table className="w-full text-sm">
                            <thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                              <tr>
                                <th className="w-24 py-1">Account</th>
                                <th className="py-1">Name</th>
                                <th className="w-40 py-1 text-right">Debit</th>
                                <th className="w-40 py-1 text-right">Credit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.lines.map((line, i) => (
                                <tr key={`${line.code}-${i}`}>
                                  <td className="py-1 font-mono text-xs">{line.code}</td>
                                  <td className="py-1">{line.name}</td>
                                  <td className="py-1 text-right font-mono tabular-nums">
                                    {line.debit ? peso(line.debit) : '—'}
                                  </td>
                                  <td className="py-1 text-right font-mono tabular-nums">
                                    {line.credit ? peso(line.credit) : '—'}
                                  </td>
                                </tr>
                              ))}
                              <tr className="border-t font-semibold">
                                <td className="py-1" />
                                <td className="py-1 text-xs text-muted-foreground">Entry totals</td>
                                <td className="py-1 text-right font-mono tabular-nums">
                                  {peso(entry.lines.reduce((s, l) => s + l.debit, 0))}
                                </td>
                                <td className="py-1 text-right font-mono tabular-nums">
                                  {peso(entry.lines.reduce((s, l) => s + l.credit, 0))}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
